/**
 * Shared bill/challan merge and invoice totals (matches invTemp / BillList export logic).
 */

export function mergeChallansForBill(challans) {
  const packagingCosts = [];
  const packagingTaxPercents = [];
  let items = [];
  let totalChallanAmount = 0;

  for (const i of challans || []) {
    packagingCosts.push(Number(i.packagingCost) || 0);
    packagingTaxPercents.push(5);

    const packagingCost = Number(i.packagingCost) || 0;
    const packagingTaxPercent = 5;
    const taxIncludedPackagingCost =
      packagingCost + (packagingCost * packagingTaxPercent) / 100;
    const itemCount = i.items ? i.items.length : 0;
    const packagingCostPerItem =
      itemCount > 0 ? taxIncludedPackagingCost / itemCount : 0;

    const challanItems = i.items
      ? i.items.map((item) => ({
        ...item,
        taxPercent: 5,
        packagingTaxPercent: 5,
        fittingAmount:
          (Number(item.fittingAmount) || 0) + packagingCostPerItem,
      }))
      : [];

    items = items.concat(challanItems);
    totalChallanAmount += Number(i.amount) || 0;
  }

  return { items, packagingCosts, packagingTaxPercents, totalChallanAmount };
}

/** Forced GST rate for sale bill create / preview (matches backend forceSaleTaxPercent). */
export const FORCED_SALE_TAX_PERCENT = 5;

/**
 * Invoice totals with every line + packaging taxed at FORCED_SALE_TAX_PERCENT.
 */
export function computeInvoiceTotalsForcedFivePercent(
  items,
  packagingCosts,
  isEligibleForIgst
) {
  const totalPkg = Array.isArray(packagingCosts)
    ? packagingCosts.reduce((a, b) => a + (Number(b) || 0), 0)
    : Number(packagingCosts) || 0;
  const forcedItems = (items || []).map((item) => ({
    ...item,
    taxPercent: FORCED_SALE_TAX_PERCENT,
    packagingTaxPercent: FORCED_SALE_TAX_PERCENT,
  }));
  const pkgTaxAmt = (totalPkg * FORCED_SALE_TAX_PERCENT) / 100;
  return computeInvoiceTotals(
    forcedItems,
    totalPkg,
    !!isEligibleForIgst,
    pkgTaxAmt
  );
}

/**
 * Bill preview from challans at forced 5% (Selected / Grouped challans summary).
 */
export function buildBillPreviewFromChallans(
  challans,
  isEligibleForIgst,
  externalDiscountPercentage = 0
) {
  const merged = mergeChallansForBill(challans);
  const inv = computeInvoiceTotalsForcedFivePercent(
    merged.items,
    merged.packagingCosts,
    isEligibleForIgst
  );
  const discPct = Number(externalDiscountPercentage) || 0;
  const disc = applyExternalDiscountOnTaxable(
    inv,
    discPct,
    !!isEligibleForIgst
  );
  const totalGstPre = isEligibleForIgst
    ? inv.totalIgst
    : inv.totalCgst + inv.totalSgst;
  const totalGst = discPct > 0 ? disc.totalTaxAfter : totalGstPre;
  return {
    taxableValue: inv.taxableValue,
    discountedTaxable: disc.discountedTaxable,
    totalGst,
    totalQty: inv.totalQuantity,
    billAmount: disc.finalGrandTotal,
    discountPercent: discPct,
    discountAmount: disc.discountAmount,
    merged,
    inv,
    disc,
  };
}

/**
 * @returns Invoice subtotals; preRoundGrand = taxable + tax before external discount / round-off.
 */
export function computeInvoiceTotals(
  items,
  TotalPackagingCostInput,
  isEligibleForIgst,
  TotalPackagingTaxAmount = 0
) {
  let TotalPackagingCost = 0;
  if (Array.isArray(TotalPackagingCostInput)) {
    TotalPackagingCost = TotalPackagingCostInput.reduce((sum, val) => sum + (Number(val) || 0), 0);
  } else {
    TotalPackagingCost = Number(TotalPackagingCostInput) || 0;
  }

  const DEFAULT_PRESCRIPTION_GST_PERCENT = 5;

  const getEffectiveTaxPercentForItem = (i, fallbackBase = 0) => {
    const raw = Number(i?.taxPercent);
    if (Number.isFinite(raw) && raw > 0) return raw;
    if (i?.isPrescriptionOrder) return DEFAULT_PRESCRIPTION_GST_PERCENT;
    return fallbackBase;
  };

  const taxPercentBase =
    (items || [])
      // If any item has a taxPercent, use it; else if prescription items exist, default to 5%.
      .map((i) => getEffectiveTaxPercentForItem(i, 0))
      .filter((n) => Number.isFinite(n) && n > 0)[0] || 0;

  const cgstPer = isEligibleForIgst ? 0 : taxPercentBase / 2;
  const sgstPer = isEligibleForIgst ? 0 : taxPercentBase / 2;
  const igstPer = isEligibleForIgst ? taxPercentBase : 0;

  let totalQuantity = 0;
  let totalAmount = 0;
  let totalCgst = 0;
  let totalSgst = 0;
  let totalIgst = 0;
  const hsnSet = new Set();

  (items || []).forEach((o) => {
    const amt = Number(o.amount) || 0;
    const qty = Number(o.quantity) || 0;
    const effTaxPercent = getEffectiveTaxPercentForItem(o, taxPercentBase);

    totalQuantity += qty;
    totalAmount += amt;
    if (o.hsnCode) hsnSet.add(o.hsnCode);

    if (isEligibleForIgst) {
      totalIgst += (effTaxPercent / 100) * amt;
    } else {
      totalCgst += (effTaxPercent / 200) * amt;
      totalSgst += (effTaxPercent / 200) * amt;
    }
  });

  if (TotalPackagingCost > 0) {
    const pkgTaxAmt = TotalPackagingTaxAmount > 0 
      ? TotalPackagingTaxAmount 
      : (isEligibleForIgst
          ? (igstPer / 100) * TotalPackagingCost
          : ((cgstPer + sgstPer) / 100) * TotalPackagingCost);

    if (isEligibleForIgst) {
      totalIgst += pkgTaxAmt;
    } else {
      totalCgst += pkgTaxAmt / 2;
      totalSgst += pkgTaxAmt / 2;
    }
  }

  const taxableValue = totalAmount + TotalPackagingCost;
  const totalTax = isEligibleForIgst ? totalIgst : totalCgst + totalSgst;
  const preRoundGrand = taxableValue + totalTax;

  const displayHsn =
    hsnSet.size === 1
      ? [...hsnSet][0]
      : hsnSet.size > 1
        ? "Various"
        : "";

  return {
    totalQuantity,
    totalAmount,
    TotalPackagingCost,
    taxableValue,
    totalCgst,
    totalSgst,
    totalIgst,
    totalTax,
    preRoundGrand,
    taxPercentBase,
    cgstPer,
    sgstPer,
    igstPer,
    displayHsn,
  };
}

/**
 * External discount as % of taxable value only; GST is scaled to the reduced base.
 * @param {ReturnType<typeof computeInvoiceTotals>} invTotals
 * @param {number} externalDiscountPercentage 0–100
 * @param {boolean} isEligibleForIgst
 */
export function applyExternalDiscountOnTaxable(
  invTotals,
  externalDiscountPercentage,
  isEligibleForIgst
) {
  const pct = Number(externalDiscountPercentage) || 0;
  const {
    taxableValue,
    totalCgst,
    totalSgst,
    totalIgst,
    totalTax,
    preRoundGrand,
  } = invTotals;

  if (pct <= 0) {
    const finalGrandTotal = Math.round(preRoundGrand);
    return {
      discountAmount: 0,
      discountedTaxable: taxableValue,
      totalCgstAfter: totalCgst,
      totalSgstAfter: totalSgst,
      totalIgstAfter: totalIgst,
      totalTaxAfter: totalTax,
      preRoundGrand,
      finalGrandTotal,
      roundOff: finalGrandTotal - preRoundGrand,
    };
  }

  const discountAmount = taxableValue * (pct / 100);
  const discountedTaxable = taxableValue - discountAmount;
  const scale =
    taxableValue > 0 ? discountedTaxable / taxableValue : 0;

  const totalCgstAfter = totalCgst * scale;
  const totalSgstAfter = totalSgst * scale;
  const totalIgstAfter = totalIgst * scale;
  const totalTaxAfter = isEligibleForIgst
    ? totalIgstAfter
    : totalCgstAfter + totalSgstAfter;

  const newPreRoundGrand = discountedTaxable + totalTaxAfter;
  const finalGrandTotal = Math.round(newPreRoundGrand);
  const roundOff = finalGrandTotal - newPreRoundGrand;

  return {
    discountAmount,
    discountedTaxable,
    totalCgstAfter,
    totalSgstAfter,
    totalIgstAfter,
    totalTaxAfter,
    preRoundGrand: newPreRoundGrand,
    finalGrandTotal,
    roundOff,
  };
}

export function computeInvoiceTotalsOld(
  items,
  packagingCosts,
  isEligibleForIgst
) {
  const DEFAULT_PRESCRIPTION_GST_PERCENT = 5;

  const getEffectiveTaxPercentForItem = (i, fallbackBase = 0) => {
    const raw = Number(i?.taxPercent);
    if (Number.isFinite(raw) && raw > 0) return raw;
    if (i?.isPrescriptionOrder) return DEFAULT_PRESCRIPTION_GST_PERCENT;
    return fallbackBase;
  };

  let TotalPackagingCost = 0;
  for (const cost of packagingCosts || []) {
    TotalPackagingCost += Number(cost) || 0;
  }

  const taxPercentBase =
    (items || [])
      // If any item has a taxPercent, use it; else if prescription items exist, default to 5%.
      .map((i) => getEffectiveTaxPercentForItem(i, 0))
      .filter((n) => Number.isFinite(n) && n > 0)[0] || 0;

  const cgstPer = isEligibleForIgst ? 0 : taxPercentBase / 2;
  const sgstPer = isEligibleForIgst ? 0 : taxPercentBase / 2;
  const igstPer = isEligibleForIgst ? taxPercentBase : 0;

  let totalQuantity = 0;
  let totalAmount = 0;
  let totalCgst = 0;
  let totalSgst = 0;
  let totalIgst = 0;
  const hsnSet = new Set();

  (items || []).forEach((o) => {
    const amt = Number(o.amount) || 0;
    const qty = Number(o.quantity) || 0;
    const effTaxPercent = getEffectiveTaxPercentForItem(o, taxPercentBase);

    totalQuantity += qty;
    totalAmount += amt;
    if (o.hsnCode) hsnSet.add(o.hsnCode);

    if (isEligibleForIgst) {
      totalIgst += (effTaxPercent / 100) * amt;
    } else {
      totalCgst += (effTaxPercent / 200) * amt;
      totalSgst += (effTaxPercent / 200) * amt;
    }
  });

  if (TotalPackagingCost > 0) {
    const pkgTaxAmt = isEligibleForIgst
      ? (igstPer / 100) * TotalPackagingCost
      : ((cgstPer + sgstPer) / 100) * TotalPackagingCost;

    if (isEligibleForIgst) {
      totalIgst += pkgTaxAmt;
    } else {
      totalCgst += pkgTaxAmt / 2;
      totalSgst += pkgTaxAmt / 2;
    }
  }

  const taxableValue = totalAmount + TotalPackagingCost;
  const totalTax = isEligibleForIgst ? totalIgst : totalCgst + totalSgst;
  const preRoundGrand = taxableValue + totalTax;

  const displayHsn =
    hsnSet.size === 1
      ? [...hsnSet][0]
      : hsnSet.size > 1
        ? "Various"
        : "";

  return {
    totalQuantity,
    totalAmount,
    TotalPackagingCost,
    taxableValue,
    totalCgst,
    totalSgst,
    totalIgst,
    totalTax,
    preRoundGrand,
    taxPercentBase,
    cgstPer,
    sgstPer,
    igstPer,
    displayHsn,
  };
}
